import { useMemo } from "react";
import {
	CalendarEvent,
	CLIENT_PROJECT_SEPARATOR,
	Project,
	ProjectsSortedByTime,
	UNNAMED_PROJECT,
} from "./App";

export function useProjectsSortedByTime(
	events: CalendarEvent[],
	addError: (e: Error) => void,
) {
	const groupedEvents = useMemo(() => {
		let res: {
			[name: string]:
				| undefined
				| {
						projects: {
							[project: string]: undefined | Project;
						};
						latestEventTime: number;
				  };
		} = {};
		for (const event of events) {
			const summaryWords = (event.summary || "").split(" ");
			const done = summaryWords.includes("DONE");
			let [customerName, projectName] = summaryWords;
			projectName = projectName ?? UNNAMED_PROJECT;
			if (!customerName.startsWith("#")) {
				addError(
					new Error(
						"Invalid event (invalid customer name): " +
							event.summary +
							", time: " +
							event.start?.dateTime,
					),
				);
				continue;
			}
			if (!projectName.startsWith("#")) {
				addError(
					new Error(
						"Invalid event (invalid or missing project name): " +
							event.summary +
							", time: " +
							event.start?.dateTime,
					),
				);
				continue;
			}
			if (!(customerName in res)) {
				res[customerName] = { projects: {}, latestEventTime: 0 };
			}
			const customer = res[customerName];
			if (customer && !(projectName in customer.projects)) {
				customer.projects[projectName] = {
					time: 0,
					latestEventTime: 0,
					done: false,
					events: [],
				};
			}
			const project = customer?.projects?.[projectName];
			if (project && event.start?.dateTime && event.end?.dateTime) {
				const startTime = new Date(event.start.dateTime).getTime();
				const endTime = new Date(event.end.dateTime).getTime();
				project.time += endTime - startTime;
				customer.latestEventTime = Math.max(
					endTime,
					customer.latestEventTime,
				);
				project.latestEventTime = Math.max(
					endTime,
					project.latestEventTime,
				);
				project.done = project.done || done;
				project.events.push(event);
			}
		}
		return res;
	}, [events]);

	const customersSortedByTime = useMemo(() => {
		const entries = Object.entries(groupedEvents);
		entries.sort(
			(a, b) =>
				(b[1]?.latestEventTime || 0) - (a[1]?.latestEventTime || 0),
		);
		return entries.map((x) => x[0]);
	}, [groupedEvents]);

	const projectsSortedByTime: ProjectsSortedByTime = useMemo(() => {
		let customerAndProjects: {
			[customerAndProjectKey: string]: undefined | Project;
		} = {};
		for (const customerName of customersSortedByTime) {
			const entries = Object.entries(
				groupedEvents[customerName]?.projects || {},
			);
			for (const [key, entry] of entries) {
				customerAndProjects[
					`${customerName}${CLIENT_PROJECT_SEPARATOR}${key}`
				] = entry;
			}
		}
		const customerAndProjectsArray = Object.entries(customerAndProjects);
		customerAndProjectsArray.sort(
			(a, b) =>
				(b[1]?.latestEventTime || 0) - (a[1]?.latestEventTime || 0),
		);
		return {
			keys: customerAndProjectsArray.map((x) => x[0]),
			projects: customerAndProjects,
		};
	}, [groupedEvents, events, customersSortedByTime]);

	return projectsSortedByTime;
}
